import React from 'react'
import {
  Alert,
  Box,
  Button,
  Collapse,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'

import DisabledReason from '../../components/system/DisabledReason'
import {
  sendInfluencerFile,
  useInvalidateAfterInfluencerUpload,
  type InfluencerUploadSummary,
} from '../../hooks/socialdash/useInfluencerUploads'

const n = (v: number) => v.toLocaleString('en-GB')
const plural = (v: number, one: string, many = `${one}s`) => `${n(v)} ${v === 1 ? one : many}`

/** The headline of a summary, in plain words. Exported for the test. */
export function summaryLine(s: InfluencerUploadSummary): string {
  const parts = [`${plural(s.rows, 'row')} read`]
  if (s.duplicateListings > 0) {
    parts.push(
      `${plural(s.duplicateListings, 'duplicate listing')} folded into ${plural(s.duplicatePosts, 'post')}`,
    )
  }
  parts.push(`${plural(s.posts, 'post')}: ${n(s.new)} new, ${n(s.updated)} updated, ${n(s.unchanged)} unchanged`)
  return parts.join(' · ')
}

/** Why Confirm is disabled, or undefined when it is not. Exported for the test. */
export function confirmReason(s: InfluencerUploadSummary | null, busy: boolean): string | undefined {
  if (busy) return 'Working…'
  if (!s) return 'Choose a file first.'
  if (s.new + s.updated === 0) return 'Nothing to import: every post in this file is already held exactly as it is.'
  return undefined
}

/**
 * Upload Sky's influencer export (#1694 follow-up): choose a file, read the
 * preview, Confirm import. The server previews unless asked to write, so
 * Confirm re-sends the SAME File object that was previewed. Nothing is ever
 * deleted: posts held but not in the file are counted and kept.
 */
export const InfluencerUpload: React.FC = () => {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const invalidate = useInvalidateAfterInfluencerUpload()
  const [file, setFile] = React.useState<File | null>(null)
  const [preview, setPreview] = React.useState<InfluencerUploadSummary | null>(null)
  const [written, setWritten] = React.useState<InfluencerUploadSummary | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [showDuplicates, setShowDuplicates] = React.useState(false)

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    // Cleared so choosing the same file again (after fixing it) fires again.
    e.target.value = ''
    if (!picked) return
    setFile(picked)
    setPreview(null)
    setWritten(null)
    setError('')
    setShowDuplicates(false)
    setBusy(true)
    try {
      setPreview(await sendInfluencerFile(picked, false))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    if (!file || !preview) return
    setBusy(true)
    setError('')
    try {
      setWritten(await sendInfluencerFile(file, true))
      invalidate()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const reset = () => {
    setFile(null)
    setPreview(null)
    setWritten(null)
    setError('')
  }

  const shown = written ?? preview

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }} data-testid="influencer-upload">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Upload an influencer file
          </Typography>
          <Typography variant="body2" color="text.secondary">
            An .xlsx or .csv with the columns of Sky’s influencer export. You see what it would change before
            anything is written.
          </Typography>
        </Box>
        <Button
          color="info"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid="influencer-upload-choose"
        >
          Choose file
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.csv,text/csv"
          hidden
          onChange={pick}
          data-testid="influencer-upload-file"
        />
      </Box>

      {busy && !shown && (
        <Typography sx={{ mt: 2 }} data-testid="influencer-upload-reading">
          Reading {file?.name}…
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="influencer-upload-error">
          {error}
        </Alert>
      )}

      {shown && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="influencer-upload-preview">
          {written ? (
            <Alert severity="success" data-testid="influencer-upload-written">
              Imported {shown.fileName}. The dashboards’ Influencer figures now include it.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Preview of <strong>{shown.fileName}</strong>
              {shown.from && ` · published ${shown.from} to ${shown.to}`}. Nothing has been written yet.
            </Typography>
          )}
          <Typography sx={{ fontWeight: 600 }} data-testid="influencer-upload-summary">
            {summaryLine(shown)}
          </Typography>

          <Table size="small" data-testid="influencer-upload-dashboards">
            <TableHead>
              <TableRow>
                <TableCell>Dashboard</TableCell>
                <TableCell align="right">Posts</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Influencers</TableCell>
                <TableCell align="right">Reach</TableCell>
                <TableCell align="right">New</TableCell>
                <TableCell align="right">Updated</TableCell>
                <TableCell align="right">Unchanged</TableCell>
                <TableCell align="right">Held, not in this file</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.dashboards.map((d) => (
                <TableRow key={d.dashboard} data-testid={`influencer-upload-dashboard-${d.dashboard}`}>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{d.dashboard}</TableCell>
                  <TableCell align="right">{n(d.posts)}</TableCell>
                  <TableCell align="right">{n(d.paidPosts)}</TableCell>
                  <TableCell align="right">{n(d.influencers)}</TableCell>
                  <TableCell align="right">{n(d.reach)}</TableCell>
                  <TableCell align="right">{n(d.new)}</TableCell>
                  <TableCell align="right">{n(d.updated)}</TableCell>
                  <TableCell align="right">{n(d.unchanged)}</TableCell>
                  <TableCell align="right">{n(d.heldNotInFile)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary">
            Teams in the file:{' '}
            {shown.fileTeams.map((t) => `${t.team} (${n(t.rows)} rows) → ${t.dashboard}`).join(' · ')}. Corporate
            has no influencer team.
          </Typography>

          {shown.duplicateListings > 0 && (
            <Alert severity="info" data-testid="influencer-upload-duplicates">
              {plural(shown.duplicatePosts, 'post was', 'posts were')} listed more than once for the same team
              (under different activities). Each is counted once, with every activity kept.
              {shown.conflicts > 0 &&
                ` ${plural(shown.conflicts, 'listing')} had different figures; the one with the larger reach was kept.`}{' '}
              <Link component="button" type="button" onClick={() => setShowDuplicates((v) => !v)} color="info.main">
                {showDuplicates ? 'Hide them' : 'Show them'}
              </Link>
              <Collapse in={showDuplicates} unmountOnExit>
                <Table size="small" sx={{ mt: 1 }} data-testid="influencer-upload-duplicates-list">
                  <TableHead>
                    <TableRow>
                      <TableCell>Dashboard</TableCell>
                      <TableCell>Influencer</TableCell>
                      <TableCell>Content ID</TableCell>
                      <TableCell align="right">Listings</TableCell>
                      <TableCell>Activities</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shown.duplicates.map((d) => (
                      <TableRow key={`${d.dashboard}|${d.contentId}`}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{d.dashboard}</TableCell>
                        <TableCell>{d.profileName}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{d.contentId}</TableCell>
                        <TableCell align="right">{d.listings}</TableCell>
                        <TableCell>{d.activities.join(' · ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {shown.duplicatePosts > shown.duplicates.length && (
                  <Typography variant="caption">
                    The first {shown.duplicates.length} of {n(shown.duplicatePosts)}.
                  </Typography>
                )}
              </Collapse>
            </Alert>
          )}
          {shown.crossDashboard > 0 && (
            <Alert severity="info" data-testid="influencer-upload-cross">
              {plural(shown.crossDashboard, 'post is', 'posts are')} listed by two or more teams. Each is one
              post, shown on its first team’s dashboard (alphabetically), with every team kept on it.
            </Alert>
          )}
          {shown.scrape && (
            <Alert
              severity={shown.scrape.unsupported > 0 ? 'warning' : 'info'}
              data-testid="influencer-upload-scrape"
            >
              {shown.written
                ? `${plural(shown.scrape.queued, 'post was', 'posts were')} queued for their comments to be scraped (posts queued by an earlier upload are not queued again). `
                : `On upload, ${plural(shown.scrape.scrapable, 'post', 'posts')} will be queued for their comments to be scraped. `}
              {shown.scrape.unsupported > 0 &&
                `${plural(shown.scrape.unsupported, 'post is', 'posts are')} on a site with no comment scraper (${shown.scrape.sites
                  .filter((s) => !s.scrapable)
                  .map((s) => `${s.site} ${n(s.posts)}`)
                  .join(', ')}): they count as posts, with no comments. `}
              By site:{' '}
              {shown.scrape.sites.map((s) => `${s.site} ${n(s.posts)}`).join(' · ')}.
            </Alert>
          )}
          {shown.heldNotInFile > 0 && (
            <Alert severity="warning" data-testid="influencer-upload-held">
              {plural(shown.heldNotInFile, 'post is', 'posts are')} held but not in this file. They are kept: an
              upload never removes posts.
            </Alert>
          )}
          {shown.errorCells > 0 && (
            <Alert severity="warning">
              {plural(shown.errorCells, 'number cell')} held a spreadsheet error (such as #DIV/0!) and{' '}
              {shown.errorCells === 1 ? 'was' : 'were'} read as 0.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button color="info" onClick={reset} disabled={busy} data-testid="influencer-upload-cancel">
              {written ? 'Done' : 'Cancel'}
            </Button>
            {!written && (
              <DisabledReason reason={confirmReason(preview, busy)}>
                <Button color="info" variant="contained" onClick={confirm} data-testid="influencer-upload-confirm">
                  {busy ? 'Importing…' : 'Confirm import'}
                </Button>
              </DisabledReason>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  )
}

export default InfluencerUpload
